import {
  useEffect,
  useId,
  useRef,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  type RefObject,
} from "react";
import { createPortal } from "react-dom";
import { useI18n } from "@wato/i18n/react";
import { cx } from "../../cx";
import { CloseIcon } from "../../rtl/icons";

const FOCUSABLE = [
  "a[href]",
  "area[href]",
  "button:not([disabled])",
  "input:not([disabled]):not([type='hidden'])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "iframe",
  "[contenteditable='true']",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

function focusableIn(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (el) => !el.hasAttribute("inert") && el.getAttribute("aria-hidden") !== "true",
  );
}

const SIZE = {
  sm: "max-w-sm",
  md: "max-w-[var(--size-dialog)]",
  lg: "max-w-2xl",
} as const;

export interface ModalProps {
  readonly open: boolean;
  /** Called on ESC, the close button and (by default) an overlay click. */
  readonly onClose: () => void;
  readonly title: string;
  readonly description?: string;
  readonly children?: ReactNode;
  /** Action row, laid out at the inline end (left in RTL). */
  readonly footer?: ReactNode;
  readonly size?: keyof typeof SIZE;
  /** Element to focus on open (default: first focusable in the body, else the dialog). */
  readonly initialFocusRef?: RefObject<HTMLElement | null>;
  readonly closeOnOverlayClick?: boolean;
  /** `sheet-on-mobile`: bottom-aligned sheet below `md`, centred dialog from `md` up (G14). */
  readonly variant?: "centered" | "sheet-on-mobile";
}

/**
 * Accessible modal dialog: portal to `<body>`, `aria-modal`, focus trap (Tab/Shift+Tab
 * cycle inside), ESC closes, focus returns to the opener, body scroll locked. The close
 * button sits at the header's inline end, so it is top-left in Arabic, top-right in English.
 */
export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = "md",
  initialFocusRef,
  closeOnOverlayClick = true,
  variant = "centered",
}: ModalProps) {
  const { t } = useI18n();
  const panelRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const descriptionId = useId();
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const { overflow } = document.body.style;
    document.body.style.overflow = "hidden";

    const panel = panelRef.current;
    const target =
      initialFocusRef?.current ??
      (bodyRef.current ? focusableIn(bodyRef.current)[0] : undefined) ??
      panel;
    target?.focus();

    return () => {
      document.body.style.overflow = overflow;
      if (opener?.isConnected) opener.focus();
    };
  }, [open, initialFocusRef]);

  if (!open || typeof document === "undefined") return null;

  function onKeyDown(event: ReactKeyboardEvent<HTMLDivElement>) {
    if (event.key === "Escape") {
      event.stopPropagation();
      onCloseRef.current();
      return;
    }
    if (event.key !== "Tab" || !panelRef.current) return;
    const items = focusableIn(panelRef.current);
    const first = items[0];
    const last = items[items.length - 1];
    if (!first || !last) {
      event.preventDefault();
      panelRef.current.focus();
      return;
    }
    const active = document.activeElement;
    const inside = active instanceof Node && panelRef.current.contains(active);
    if (event.shiftKey && (active === first || !inside || active === panelRef.current)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (active === last || !inside)) {
      event.preventDefault();
      first.focus();
    }
  }

  return createPortal(
    <div
      className={cx(
        "fixed inset-0 z-50 flex items-center justify-center p-4",
        variant === "sheet-on-mobile" && "max-md:items-end max-md:p-0",
      )}
    >
      <div
        aria-hidden="true"
        data-testid="modal-overlay"
        className="absolute inset-0 bg-neutral-950/50"
        onClick={closeOnOverlayClick ? () => onCloseRef.current() : undefined}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        tabIndex={-1}
        onKeyDown={onKeyDown}
        className={cx(
          "relative flex max-h-full w-full flex-col rounded-2xl bg-surface text-fg shadow-lg focus:outline-none",
          SIZE[size],
          variant === "sheet-on-mobile" &&
            "max-md:max-h-[85dvh] max-md:max-w-none max-md:rounded-b-none max-md:rounded-t-2xl max-md:pb-[env(safe-area-inset-bottom)]",
        )}
      >
        <header className="flex items-start justify-between gap-4 border-b border-line px-5 py-4">
          <div className="flex min-w-0 flex-col gap-1">
            <h2 id={titleId} className="text-lg font-semibold">
              {title}
            </h2>
            {description && (
              <p id={descriptionId} className="text-sm text-fg-muted">
                {description}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={() => onCloseRef.current()}
            aria-label={t("ui.modal.close")}
            className="-me-2 -mt-1 flex size-9 shrink-0 items-center justify-center rounded-xl text-fg-muted hover:bg-surface-sunken hover:text-fg"
          >
            <CloseIcon />
          </button>
        </header>
        <div ref={bodyRef} className="min-h-0 overflow-y-auto px-5 py-4">
          {children}
        </div>
        {footer && (
          <footer className="flex flex-wrap items-center justify-end gap-2 border-t border-line px-5 py-3">
            {footer}
          </footer>
        )}
      </div>
    </div>,
    document.body,
  );
}
